import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@anytag/shared-utils';
import { useDebounce } from '@anytag/shared-hooks';
import { ComboboxSearch, ComboboxSearchProps } from '@anyui/core';
import { useSelectContext } from '../select-context';

export interface SelectSearchProps extends Pick<ComboboxSearchProps, 'className' | 'placeholder'> {
  alwaysShow?: boolean;
  selectedValues: string[];
}

export const SelectSearch = ({ className, alwaysShow, placeholder, selectedValues }: SelectSearchProps) => {
  const { t } = useTranslation();
  const { debounce } = useDebounce();
  const ref = useRef<HTMLInputElement>(null);
  const {
    search,
    setSearch,
    selectProps: { loading, options, classNames, searchable = true, onSearch, searchProps }
  } = useSelectContext();

  // Must be ??: when context is not provided, we use props otherwise use context
  const isAlwaysShow = searchProps?.alwaysShow ?? alwaysShow;

  return searchable && (isAlwaysShow || options?.length) ? (
    <ComboboxSearch
      ref={ref}
      value={search}
      disabled={loading || (!onSearch && !options?.length)} // If onSearch is provided, this input always visible for searching
      className={cn('mb-2', className, classNames?.search)}
      placeholder={(searchProps?.placeholder ?? placeholder) || t<string>('Search.Search')}
      onValueChange={(value) => {
        setSearch(value);

        if (onSearch) {
          // Debounce onSearch to avoid excessive API calls while typing
          debounce(
            async () =>
              await onSearch(value, selectedValues).then(() => {
                ref.current?.focus(); // Focus the input after the search is completed
              }),
            500
          );
        }
      }}
    />
  ) : null;
};
